// Acquisition first-touch attribution.
// ----------------------------------------------------------------------------
// Captured once on the first landing that carries UTM / click-id params, stored
// client-side (cookie + localStorage), mirrored onto the PostHog person via
// register_once (see src/components/analytics/FirstTouchCapture.tsx), and
// persisted to the Supabase profile at signup (see handlerUserCreatedLog in
// src/lib/server/actions.ts). This is what lets a subscriber who converts days
// after the ad click still be credited to the campaign that acquired them.
//
// All client storage access is window/document-guarded so this module is safe
// to import from server code (which only uses the pure parse/sanitize helpers).

export type FirstTouch = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ttclid?: string; // TikTok click id
  gclid?: string; // Google click id
  fbclid?: string; // Meta click id
  referrer?: string;
  landing_path?: string;
  first_touch_at?: string;
};

export const FIRST_TOUCH_KEY = "workshop_first_touch";

// The query params that, when present, mark a deliberate inbound campaign click.
const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "ttclid",
  "gclid",
  "fbclid",
] as const;

const MAX_LEN = 256;
const FIRST_TOUCH_TTL_DAYS = 180;
const CHECKOUT_ATTRIBUTION_KEYS = [
  ...ATTRIBUTION_PARAMS,
  "landing_path",
  "first_touch_at",
] as const;

function clean(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_LEN);
}

// Pure: build a FirstTouch from a raw query string (+ referrer/path). Returns
// null unless at least one attribution param is present, so plain organic
// landings never overwrite an earlier campaign touch. No globals — testable.
export function parseFirstTouchFromQuery(
  search: string,
  referrer?: string,
  path?: string,
  nowIso?: string,
): FirstTouch | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const ft: FirstTouch = {};
  for (const key of ATTRIBUTION_PARAMS) {
    const v = clean(params.get(key));
    if (v) (ft as Record<string, string>)[key] = v;
  }
  if (Object.keys(ft).length === 0) return null;
  const ref = clean(referrer);
  if (ref) ft.referrer = ref;
  const p = clean(path);
  if (p) ft.landing_path = p;
  ft.first_touch_at = nowIso ?? new Date().toISOString();
  return ft;
}

// Server-safe sanitizer for an untrusted first-touch object received from the
// client. This is low-stakes analytics attribution, NOT a security boundary —
// it only keeps known keys, coerces to bounded strings, and drops everything
// else so a malicious body can't inject arbitrary columns downstream.
export function sanitizeFirstTouch(raw: unknown): FirstTouch | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Record<string, unknown>;
  const ft: FirstTouch = {};
  for (const key of [...ATTRIBUTION_PARAMS, "referrer", "landing_path"] as const) {
    const v = clean(typeof src[key] === "string" ? (src[key] as string) : undefined);
    if (v) (ft as Record<string, string>)[key] = v;
  }
  // Only keep a client-supplied timestamp if it's a real, parseable date, so a
  // malformed value can never reach the timestamptz column and fail the write.
  if (typeof src.first_touch_at === "string") {
    const fta = src.first_touch_at.slice(0, 64);
    if (!Number.isNaN(Date.parse(fta))) ft.first_touch_at = fta;
  }
  return Object.keys(ft).length ? ft : null;
}

// Pure: compact attribution payload safe to forward to checkout creation.
// It intentionally excludes referrer so Shopify URLs/orders do not store long
// or privacy-noisy third-party URLs, while keeping campaign and click ids.
export function checkoutAttribution(raw: unknown): FirstTouch | null {
  const ft = sanitizeFirstTouch(raw);
  if (!ft) return null;
  const out: FirstTouch = {};
  for (const key of CHECKOUT_ATTRIBUTION_KEYS) {
    const value = ft[key];
    if (value) (out as Record<string, string>)[key] = value;
  }
  return Object.keys(out).length ? out : null;
}

// --- client-only storage (window/document-guarded) ------------------------

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function readFirstTouch(): FirstTouch | null {
  if (typeof window === "undefined") return null;
  try {
    const fromLs = window.localStorage.getItem(FIRST_TOUCH_KEY);
    const raw = fromLs ?? readCookie(FIRST_TOUCH_KEY);
    if (!raw) return null;
    return sanitizeFirstTouch(JSON.parse(raw));
  } catch {
    return null;
  }
}

// Capture first-touch on landing. First write wins (idempotent across visits);
// returns the stored FirstTouch — existing or newly captured — or null if this
// visit carries no attribution params and none was stored before.
export function captureFirstTouch(): FirstTouch | null {
  if (typeof window === "undefined") return null;
  try {
    const existing = readFirstTouch();
    if (existing) return existing;
    const ft = parseFirstTouchFromQuery(
      window.location.search,
      typeof document !== "undefined" ? document.referrer : undefined,
      window.location.pathname,
    );
    if (!ft) return null;
    const serialized = JSON.stringify(ft);
    try {
      window.localStorage.setItem(FIRST_TOUCH_KEY, serialized);
    } catch {
      // localStorage can throw in private mode / quota — cookie still carries it
    }
    writeCookie(FIRST_TOUCH_KEY, serialized, FIRST_TOUCH_TTL_DAYS);
    return ft;
  } catch {
    return null;
  }
}
